import { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { StyledChip } from 'components/styled/new-content-chip';
import { useTranslation } from 'react-i18next';
import { SCREEN_SIZE_MD } from 'constants/frontend-contsants';
import { creatorProfileSelector } from '../../store/creators.selectors';

import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import Typography from '@mui/material/Typography';
import ImageListItem from '@mui/material/ImageListItem';
import Grid from '@mui/material/Grid';
import styled from '@emotion/styled';
import useWindowDimensions from 'hooks/use-window-dimensions';
import { colors } from 'constants/colors';

const LockedGrid = styled(Grid)({
  position: 'absolute',
  left: '0',
  top: 0,
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  color: colors.default.info,
  opacity:0.8,
  cursor: 'pointer',

  '&:hover': {
    opacity:1
  },
});

const VideoLength = styled('span')({
  position: 'absolute',
  bottom: '10px',
  right: '10px',
  fontFamily: 'Open Sans',
  fontSize: '18px',
  color: colors.default.info,
  fontWeight: '700',
  lineHeight: '25px',
  letterSpacing: '-0.04em',
});

interface AssetsListItemProps {
  asset: any;
  index: number;
  handlePreviewOpen: (asset: any, index: number) => any;
}

const AssetsListItem: FC<AssetsListItemProps> = (props) => {
  const { asset, index, handlePreviewOpen } = props;

  const { t } = useTranslation();
  const { width } = useWindowDimensions();
  const profile = useSelector(creatorProfileSelector);

  const isToShowNewContent = useMemo(() => {
    if (!profile || !profile.subscription?.id) {
      return false;
    }

    const dateDifference =
      new Date(profile.roll.lastViewedAt).getTime() -
      new Date(asset.createdAt).getTime();

    if (dateDifference < 0) {
      return true;
    }
  }, [profile, asset]);

  const formatVideoTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  };

  if (!profile) {
    return <></>;
  }
  return (
    <ImageListItem
      rows={asset.rows}
      cols={asset.cols}
      sx={{
        borderRadius: '7px',
        overflow: 'hidden',
        position: 'relative',
        cursor: 'pointer',
      }}
    >
      {asset.type !== 'video' && (
        <img
          alt={profile.username}
          src={asset.content}
          style={{ aspectRatio: 1 }}
          onClick={handlePreviewOpen(asset, index)}
        />
      )}
      {asset.type === 'video' && (
        <video
          style={{
            aspectRatio: 1,
            objectFit: 'cover',
            width: '100%',
            height: '100%',
            display: 'block',
          }}
          controls
        >
          <source src={asset.content} />
        </video>
      )}

      {isToShowNewContent && (
        <StyledChip
          label={t('statuses.new')}
          variant="filled"
          color="dark-gray"
          sx={{ borderTopLeftRadius: 0 }}
        />
      )}

      {asset.videoLength && (
        <VideoLength>{formatVideoTime(asset.videoLength)}</VideoLength>
      )}

      {asset.locked && (
        <LockedGrid onClick={handlePreviewOpen(asset, index)}>
          <VisibilityOffIcon
            className="icon"
            color="inherit"
            sx={{ fontSize: '46px' }}
          />
          {asset.cols === 2 && (
            <>
              <br />
              <Typography
                className="text"
                variant="h3"
                textTransform="uppercase"
                fontFamily="Avenir"
                color="inherit"
                fontSize="21px"
              >
                {width > SCREEN_SIZE_MD
                  ? t('buttons.click-to-view')
                  : t('buttons.tap-to-view')}
              </Typography>
            </>
          )}
        </LockedGrid>
      )}
    </ImageListItem>
  );
};

export default AssetsListItem;
